import DOMPurify from 'dompurify';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { generatePath, useParams } from 'react-router';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { DisplayM, HeadlineL } from '@sorare/core/src/atoms/typography';
import { useSeoContext } from '@sorare/core/src/contexts/seo';

import { Header } from '#src/components/Header/index.tsx';
import { SubmitRequest } from '#src/components/SubmitRequest/index.tsx';
import {
  HELP_ARTICLE,
  LINKS_RX,
  NEW_REQUEST_HASH,
  NEW_REQUEST_RX,
} from '#src/constants/routes.ts';
import { useArticle } from '#src/hooks/zendesk.ts';
import { categoryPath, sectionPath } from '#src/lib/path.ts';

import { Shortcode } from './Shortcode/index.tsx';

const messages = defineMessages({
  wasItHelpful: {
    defaultMessage: 'Was this article helpful?',
  },
});

const WasItHelpful = styled(Vertical).attrs({ gap: 3 })`
  padding-top: var(--quadruple-unit);
  text-align: center;
  width: fit-content;
  margin: 0 auto;
`;

const Content = styled.div`
  max-width: 800px;
  font: var(--t-20);
  line-height: 140%;

  /* all this styling logic is because we try to revert everything to what you expect to see
   * through the Zendesk WYSIWYG - just now we need to do it for everything EXCEPT the
   * shortcode's components */
  & a:not(& [data-sorare-shortcode-placeholder] *) {
    color: var(--c-link);
    text-underline-offset: 2px;
    text-decoration: underline;

    &:hover {
      color: var(--c-link-hover);
    }
  }

  & img:not(& [data-sorare-shortcode-placeholder] *) {
    max-width: 100% !important;
    min-width: 50% !important;
    height: auto !important;
    margin: var(--double-unit) 0;
  }

  & p:not(& [data-sorare-shortcode-placeholder] *) {
    margin: var(--double-unit) 0;
  }

  & ul:not(& [data-sorare-shortcode-placeholder] *),
  & ol:not(& [data-sorare-shortcode-placeholder] *) {
    padding: revert;

    & li {
      padding: revert;
      list-style: revert;
    }
  }
`;

const ShortcodeComponents = styled.div`
  display: none;
`;

const SHORTCODES_RX = /\[\[(.+)\]\]/g;

const getExcerpt = (article: { body: string }) => {
  const div = document.createElement('div');
  div.innerHTML = DOMPurify.sanitize(article.body);
  return (div.textContent || div.innerText || '').split('\n')[0] || '';
};

const Article = () => {
  const { articleId } = useParams();
  const { category, section, article } = useArticle(articleId);
  const { setPageMetadata } = useSeoContext();

  // this effect replaces the shortcode placeholders with the actual content generated below
  // we run it after the component has been rendered to ensure the content is available
  // this hack is necessary because the shortcode content can be nested pretty much anywhere within the HMTML
  // article content (rendered through `dangerouslySetInnerHTML`) so it's not possible to render it in place with React
  useEffect(() => {
    if (!article || !section || !category) return;

    if (article)
      setPageMetadata(article.title, { description: getExcerpt(article) });

    document
      .querySelectorAll('div[data-sorare-shortcode-placeholder]:empty')
      .forEach(node => {
        const shortcode = (node as HTMLDivElement).dataset
          .sorareShortcodePlaceholder;
        const content = document.querySelector(
          `div[data-sorare-shortcode-component="${shortcode}"]`
        )!;
        node.replaceChildren(content);
      });
  }, [article, category, section, setPageMetadata]);

  if (!article || !section || !category) {
    return <LoadingIndicator fullHeight />;
  }

  const shortcodes: Record<string, ReactNode> = {};
  const html = DOMPurify.sanitize(
    article.body
      // replace shortcodes with placeholders
      .replaceAll(SHORTCODES_RX, (match, shortcode) => {
        if (shortcode === 'transactions-history') {
          shortcodes[shortcode] = <Shortcode code={shortcode} />;
          return `<div data-sorare-shortcode-placeholder="${shortcode}"></div>`;
        }
        return match;
      })
      // ensure all links are internal
      .replaceAll(
        LINKS_RX,
        generatePath(HELP_ARTICLE, { articleId: '$1', slug: '-' })
      )
      // ensure all new requests links are internal
      .replaceAll(NEW_REQUEST_RX, NEW_REQUEST_HASH),
    {
      ADD_TAGS: ['iframe'],
    }
  );

  const sections = [
    {
      name: category.name,
      to: categoryPath(category),
    },
    {
      name: section.name,
      to: sectionPath(section),
    },
  ];

  return (
    <Vertical gap={3}>
      <Header sections={sections} />
      <DisplayM as="h1">{article.title}</DisplayM>
      {/* eslint-disable-next-line react/no-danger */}
      <Content
        dangerouslySetInnerHTML={{
          __html: html.replace(
            /\s*(color|background(?:-color)?)\s*:\s*[^;]+(;|")/gi,
            ''
          ),
        }}
      />
      <WasItHelpful>
        <HeadlineL as="h3">
          <FormattedMessage {...messages.wasItHelpful} />
        </HeadlineL>
        <SubmitRequest />
      </WasItHelpful>
      <ShortcodeComponents>
        {Object.entries(shortcodes).map(([shortcode, node]) => (
          <div key={shortcode} data-sorare-shortcode-component={shortcode}>
            {node}
          </div>
        ))}
      </ShortcodeComponents>
    </Vertical>
  );
};

export default Article;
